import PropTypes from "prop-types"
import React from "react"
import { useMutation, useQueryClient } from "react-query"
import RevenueMappingStatus from "src/main/Accounting/RevenueMappingStatus"

import Form from "src/components/Form"
import Tooltip from "src/components/Tooltip"

import { updateGeneralLedgerCodeMapping } from "src/api/Accounting"

import { useToast } from "src/hooks/use_toast"

const RevenueMappingRow = ({
  row,
  codeOptions,
  codeOptionGroups,
  marinaSlug,
  queryKey,
}) => {
  const queryClient = useQueryClient()
  const showToast = useToast()
  const savedValue = row.generalLedgerCodeId?.toString() ?? ""
  const selectId = `revenue-mapping-${row.categoryId}`
  const { mutate, isLoading, variables } = useMutation(
    (value) =>
      updateGeneralLedgerCodeMapping(marinaSlug, {
        categories: { [row.categoryId]: value },
        type: "accounting",
      }),
    {
      onSuccess: (data) => {
        showToast(data.message, { type: "success", duration: 3 })
      },
      onError: (error) => {
        showToast(error.toString(), { type: "error", duration: 5 })
      },
      // The status and message come from the server's check, so refetch once the save has landed. Returning the
      // refetch keeps the mutation loading until it lands.
      onSettled: () => queryClient.invalidateQueries(queryKey),
    }
  )
  // The pick shows while it saves; after that, whatever the server kept. They can differ: clearing a field deletes
  // one mapping row, and a duplicate row for the same category survives.
  const selectValue = isLoading ? variables : savedValue

  const handleChange = (event) => mutate(event.target.value)

  const { untyped, groups } = codeOptionGroups
  const selectedOption = codeOptions.find(
    (option) => option.value.toString() === selectValue
  )
  const renderOption = (option) => (
    <option key={option.value} value={option.value}>
      {option.label}
    </option>
  )

  return (
    <div className="grid grid-cols-1 items-start gap-x-4 gap-y-2 border-t border-gray-100 px-5 py-4 md:grid-cols-[minmax(0,280px)_minmax(0,560px)]">
      {/* As tall as the select, so the name lines up with it rather than with the lines beneath it. */}
      <div className="flex min-h-[40px] items-center gap-1">
        <Form.Label htmlFor={selectId} noMargin>
          <span className="text-base font-semibold">{row.label}</span>
        </Form.Label>
        <Tooltip
          text={row.tooltip}
          placement="right"
          variant="dark"
          maxWidth="400px"
          tabIndex={0}
        >
          <span className="flex h-10 w-10 items-center pl-1">
            <i
              className="fa-solid fa-circle-info text-gray-600"
              aria-hidden="true"
            />
            <span className="sr-only">About {row.label}</span>
          </span>
        </Tooltip>
      </div>
      <div className="flex flex-col items-start gap-1.5">
        <Form.Select
          id={selectId}
          value={selectValue}
          onChange={handleChange}
          hasErrors={row.level === "error"}
          hasWarning={row.level === "warning"}
        >
          <option value="">No account</option>
          {untyped.map(renderOption)}
          {groups.map(([accountTypeLabel, options]) => (
            <optgroup key={accountTypeLabel} label={accountTypeLabel}>
              {options.map(renderOption)}
            </optgroup>
          ))}
        </Form.Select>
        {selectedOption?.accountTypeLabel && (
          <span className="text-sm text-gray-700">
            <span className="font-semibold">Account type:</span>{" "}
            {selectedOption.accountTypeLabel}
          </span>
        )}
        {row.status && (
          <RevenueMappingStatus status={row.status} message={row.message} />
        )}
      </div>
    </div>
  )
}

RevenueMappingRow.propTypes = {
  row: PropTypes.shape({
    categoryId: PropTypes.number.isRequired,
    generalLedgerCodeId: PropTypes.number,
    label: PropTypes.string.isRequired,
    tooltip: PropTypes.string.isRequired,
    status: PropTypes.string,
    level: PropTypes.oneOf(["error", "warning"]),
    message: PropTypes.string,
  }).isRequired,
  codeOptions: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.number.isRequired,
      label: PropTypes.string.isRequired,
      accountTypeLabel: PropTypes.string,
    })
  ).isRequired,
  codeOptionGroups: PropTypes.shape({
    untyped: PropTypes.array.isRequired,
    groups: PropTypes.array.isRequired,
  }).isRequired,
  marinaSlug: PropTypes.string.isRequired,
  queryKey: PropTypes.array.isRequired,
}

export default RevenueMappingRow
